/**
 * Folders on employee profiles (`/api/employee-folders`) and the company
 * Templates & Policy Library (`/api/library`). Folders only group documents:
 * filing is many-to-many and deleting a folder never deletes what's in it.
 * See docs/document-folders.md.
 */

import { apiFetch, apiUrl, OFFLINE_MESSAGE } from "@/core/lib/api";
import { maybeUpsellPlanLimit, PlanLimitError } from "@/core/lib/plan-limit-store";

export class FolderApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const isForm = init?.body instanceof FormData;
  let res: Response;
  try {
    res = await apiFetch(apiUrl(path), {
      credentials: "include",
      ...init,
      // A FormData body gets its multipart boundary from the browser.
      headers: isForm ? init?.headers : { "Content-Type": "application/json", ...init?.headers },
    });
  } catch (err) {
    if (err instanceof TypeError && /fetch/i.test(err.message)) {
      throw new FolderApiError(OFFLINE_MESSAGE, 0);
    }
    throw err;
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    if (maybeUpsellPlanLimit(res.status, data)) {
      throw new PlanLimitError(String((data as { error?: unknown }).error ?? ""));
    }
    const message =
      (data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : null) ?? `Request failed (${res.status})`;
    throw new FolderApiError(message, res.status);
  }
  return data as T;
}

/** The message to show for a failed folder call. */
export function folderErrorMessage(err: unknown): string {
  return err instanceof FolderApiError || err instanceof PlanLimitError
    ? err.message
    : "Please try again.";
}

export type FolderVisibility = "shared" | "private";

export type Folder = {
  id: string;
  name: string;
  visibility: FolderVisibility;
  /** Employee folders only: null = on every profile, set = a one-off. */
  employeeId: string | null;
  /** Whether the signed-in user made it. */
  mine: boolean;
  creatorName: string | null;
  createdAt: string;
};

export type Filing = { folderId: string; itemType: string; itemId: string };

/** Folder ids per `${itemType}:${itemId}`. */
export function filingsByItem(filings: Filing[]): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const f of filings) {
    const key = `${f.itemType}:${f.itemId}`;
    out.set(key, [...(out.get(key) ?? []), f.folderId]);
  }
  return out;
}

// ── Employee folders ────────────────────────────────────────────────────────

export type EmployeeItemType = "document" | "attachment";

export type EmployeeFolders = { folders: Folder[]; filings: Filing[] };

export const employeeFoldersKey = (employeeId: string) => ["employee-folders", employeeId] as const;

export function employeeFoldersQuery(employeeId: string) {
  return {
    queryKey: employeeFoldersKey(employeeId),
    queryFn: () =>
      request<EmployeeFolders>(
        `/api/employee-folders?employeeId=${encodeURIComponent(employeeId)}`,
      ),
  };
}

export async function createEmployeeFolder(input: {
  name: string;
  visibility: FolderVisibility;
  employeeId: string | null;
}): Promise<Folder> {
  const data = await request<{ folder: Folder }>("/api/employee-folders", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data.folder;
}

export async function updateEmployeeFolder(
  id: string,
  patch: { name?: string; visibility?: FolderVisibility },
): Promise<Folder> {
  const data = await request<{ folder: Folder }>(`/api/employee-folders/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
  return data.folder;
}

export function deleteEmployeeFolder(id: string): Promise<void> {
  return request<void>(`/api/employee-folders/${id}`, { method: "DELETE" });
}

export function setEmployeeFilings(input: {
  employeeId: string;
  itemType: EmployeeItemType;
  itemId: string;
  folderIds: string[];
}): Promise<{ folderIds: string[] }> {
  return request("/api/employee-folders/memberships", {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

// ── Library ─────────────────────────────────────────────────────────────────

export type LibraryItemType = "file" | "document" | "policy" | "template";

export type LibraryItem = {
  itemType: LibraryItemType;
  id: string;
  name: string;
  detail: string;
  folderIds: string[];
  updatedAt: string;
  file?: {
    contentType: string;
    sizeBytes: number | null;
    note: string | null;
    uploaderName: string | null;
    downloadUrl: string;
  };
};

export type Library = { folders: Folder[]; items: LibraryItem[] };

export const libraryKey = ["library"] as const;

export const libraryQuery = {
  queryKey: libraryKey,
  queryFn: async (): Promise<Library> => {
    const data = await request<Library>("/api/library");
    return {
      ...data,
      items: data.items.map((item) =>
        item.file
          ? { ...item, file: { ...item.file, downloadUrl: apiUrl(item.file.downloadUrl) } }
          : item,
      ),
    };
  },
};

export async function createLibraryFolder(input: {
  name: string;
  visibility: FolderVisibility;
}): Promise<Folder> {
  const data = await request<{ folder: Folder }>("/api/library/folders", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data.folder;
}

export async function updateLibraryFolder(
  id: string,
  patch: { name?: string; visibility?: FolderVisibility },
): Promise<Folder> {
  const data = await request<{ folder: Folder }>(`/api/library/folders/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
  return data.folder;
}

export function deleteLibraryFolder(id: string): Promise<void> {
  return request<void>(`/api/library/folders/${id}`, { method: "DELETE" });
}

export function setLibraryFilings(input: {
  itemType: LibraryItemType;
  itemId: string;
  folderIds: string[];
}): Promise<{ folderIds: string[] }> {
  return request("/api/library/memberships", { method: "PUT", body: JSON.stringify(input) });
}

export async function uploadLibraryFile(file: File, folderIds: string[]): Promise<LibraryItem> {
  const form = new FormData();
  form.append("file", file);
  for (const id of folderIds) form.append("folderId", id);
  const data = await request<{ item: LibraryItem }>("/api/library/files", {
    method: "POST",
    body: form,
  });
  return data.item;
}

export function deleteLibraryFile(id: string): Promise<void> {
  return request<void>(`/api/library/files/${id}`, { method: "DELETE" });
}

export type LibraryCandidate = { id: string; name: string; detail: string; updatedAt: string };

export async function searchLibraryCandidates(
  type: "document" | "policy" | "template",
  q: string,
): Promise<LibraryCandidate[]> {
  const params = new URLSearchParams({ type });
  if (q.trim()) params.set("q", q.trim());
  const data = await request<{ items: LibraryCandidate[] }>(`/api/library/candidates?${params}`);
  return data.items;
}

/** Accepted library uploads, for the file picker. Mirrors the API allowlist. */
export const LIBRARY_ACCEPT =
  ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.png,.jpg,.jpeg,.webp";
